// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import logoImg from "../assets/logo.png";
import { LanguageSelector } from '../i18n';

const ADMIN_TEAM = [
  "rmiskulin@gmail.com",
  "R.miskulin@miskulintechnologies.com",
  "criodtech@gmail.com",
  "J.lutz@miskulintechnologies.com",
  "tyler.hodgson32@gmail.com",
  "threewayindustries@gmail.com",
  "desbachynski@gmail.com",
  "lucas.s.mason@gmail.com",
  "leemehling@hotmail.com",
  "cloricer77@gmail.com",
  "tyson@canstorr.com",
  "jhansen860@yahoo.com",
  "littlesderek@gmail.com",
  "laken.weston@live.ca",
  "che@rosebudcannabisfarms.ca",
  "shanon@lanewaymedia.ca",
  "amandakristaleigh@hotmail.com",
  "demo@iqapps.dev",
  "r.miskulin@mtforge.ai",
  "T.hodgson@miskulintechnologies.com",
  "T.schenher@miskulintechnologies.com",
  "D.bachynski@miskulintechnologies.com",
  "L.mason@miskulintechnologies.com",
  "L.mehling@miskulintechnologies.com",
  "C.robertson@miskulintechnologies.com",
  "T.becker@miskulintechnologies.com",
  "H.blahut@miskulintechnologies.com",
  "dblahut@sastel.net",
  "D.blahut@miskulintechnologies.com",
  "A.benesocky@miskulintechnologies.com",
  "J.hansen@miskulintechnologies.com",
  "D.little@miskulintechnologies.com",
  "L.weston@miskulintechnologies.com",
  "C.leblanc@miskulintechnologies.com",
  "S.lutz@miskulintechnologies.com",
  "A.henwood@miskulintechnologies.com",
  // Mt Forge team
  "j.lutz@mtforge.ai",
  "t.schenher@mtforge.ai",
  "c.leblanc@mtforge.ai",
  "d.bachynski@mtforge.ai",
];

const OVERWATCH_TEAM = [
  "J.lutz@miskulintechnologies.com",
  "R.miskulin@miskulintechnologies.com",
  "tyler.hodgson32@gmail.com",
  "desbachynski@gmail.com",
  "lucas.s.mason@gmail.com",
  "leemehling@hotmail.com",
  "cloricer77@gmail.com",
  "tyson@canstorr.com",
];

export function getEffectiveRole(user) {
  if (!user) return "member";
  if (ADMIN_TEAM.map(e => e.toLowerCase()).includes((user.email || '').toLowerCase())) return "owner";
  if (user.email === "demo@iqapps.dev") return "demo";
  return user.role || "member";
}

export { ADMIN_TEAM, OVERWATCH_TEAM };
const NAV_SECTIONS = [
  { label: "Dev Page", roles: ["owner"], items: [
    { path: "/dev", label: "Dev Tools" },
  ]},
  { label: "Command", roles: ["owner", "director"], items: [
    { path: "/overwatch", label: "Overwatch" },
    { path: "/dashboard", label: "Dashboard" },
    { path: "/meeting-brief", label: "Meeting Brief" },
    { path: "/app-hub", label: "App Hub" },
    { path: "/nda-tracker", label: "NDA Tracker" },
    { path: "/sign-log", label: "Sign Log" },
    { path: "/people", label: "People" },
  ]},
  { label: "Tasks", roles: ["owner", "director", "manager", "member", "demo"], items: [
    { path: "/tasks", label: "Task Board" },
    { path: "/my-tasks", label: "My Tasks" },
    { path: "/team-tasks", label: "Team Tasks" },
    { path: "/sprints", label: "Sprints" },
  ]},
  { label: "People", roles: ["owner", "director", "manager"], items: [
    { path: "/team", label: "Team Directory" },
    { path: "/roles", label: "Roles & Permissions" },
    { path: "/performance", label: "Performance" },
  ]},
  { label: "CRM", roles: ["owner", "director", "manager"], items: [
    { path: "/contacts", label: "Contacts" },
    { path: "/deals", label: "Deals Pipeline" },
    { path: "/client-meetings", label: "Client Meetings" },
    { path: "/follow-ups", label: "Follow-Ups" },
  ]},
  { label: "Communications", roles: ["owner", "director", "manager", "member", "demo"], items: [
    { path: "/team-chat", label: "Team Chat" },
    { path: "/activity", label: "Activity Feed" },
    { path: "/announcements", label: "Bulletins" },
    { path: "/meeting-notes", label: "Meeting Notes" },
    { path: "/shared-folders", label: "Shared Folders" },
  ]},
  { label: "Site Visits", roles: ["owner", "director", "manager"], items: [
    { path: "/visit-log", label: "Visit Log" },
    { path: "/visit-reports", label: "Visit Reports" },
  ]},
  { label: "Phone Calls", roles: ["owner", "director", "manager"], items: [
    { path: "/call-log", label: "Call Log" },
    { path: "/call-reminders", label: "Call Reminders" },
    { path: "/call-scripts", label: "Call Scripts" },
  ]},
  { label: "Projects", roles: ["owner", "director", "manager", "member", "demo"], items: [
    { path: "/projects", label: "Active Projects" },
    { path: "/archive", label: "Project Archive" },
    { path: "/milestones", label: "Milestones" },
  ]},
  { label: "Reports", roles: ["owner", "director", "manager"], items: [
    { path: "/weekly-report", label: "Weekly Report" },
    { path: "/kpi", label: "KPI Dashboard" },
    { path: "/time-tracking", label: "Time Tracking" },
  ]},
  { label: "Licensing", roles: ["owner", "director"], items: [
    { path: "/licenses", label: "License Manager" },
  ]},
  { label: "System", roles: ["owner", "director", "manager", "member", "demo"], items: [
    { path: "/profile", label: "Profile" },
  ]},
];
export default function Layout({ children }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState(["Command", "Tasks", "Communications"]);
  const [viewAsRole, setViewAsRole] = useState(null);
  const actualRole = getEffectiveRole(user);
  const role = viewAsRole || actualRole;

  const toggleSection = (label) => {
    setExpandedSections(prev =>
      prev.includes(label) ? prev.filter(s => s !== label) : [...prev, label]
    );
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const userInitials = user?.full_name
    ? user.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.slice(0, 2).toUpperCase() || "U";

  const filteredSections = NAV_SECTIONS.filter(s => s.roles.includes(role));

  return (
    <div className="layout">
      <aside className={"sidebar " + (sidebarOpen ? "open" : "collapsed")}>
        <div className="sidebar-header">
          <div className="brand" onClick={() => navigate("/dashboard")} style={{ cursor: "pointer" }}>
            <img src={logoImg} alt="MT Forge" style={{ height: 36, marginRight: 8, borderRadius: 6 }} />
            {sidebarOpen && (
              <div className="brand-text">
                <span className="brand-name">MT Forge</span>
                <span className="brand-sub">Internal Operations</span>
              </div>
            )}
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? "←" : "→"}
          </button>
        </div>
        <nav className="sidebar-nav">
          {filteredSections.map(section => (
            <div key={section.label} className="nav-section">
              <button className="nav-section-header" onClick={() => toggleSection(section.label)}>
                <span className="nav-section-label">{section.label}</span>
                <span className={"nav-section-arrow " + (expandedSections.includes(section.label) ? "expanded" : "")}>
                  {expandedSections.includes(section.label) ? "▼" : "▶"}
                </span>
              </button>
              {expandedSections.includes(section.label) && sidebarOpen && (
                <div className="nav-section-items">
                  {section.items.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={"nav-item " + (location.pathname === item.path ? "active" : "")}
                    >
                      <span className="nav-item-label">{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {actualRole === 'owner' && sidebarOpen && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginBottom: 8 }}>
                View As Role
              </div>
              {['director', 'manager', 'member'].map(r => (
                <button key={r} onClick={() => setViewAsRole(viewAsRole === r ? null : r)}
                  style={{
                    display: 'block', width: '100%', padding: '6px 10px', marginBottom: 4,
                    background: viewAsRole === r ? '#4F46E5' : 'rgba(255,255,255,0.05)',
                    color: viewAsRole === r ? '#fff' : '#94a3b8',
                    border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                    textAlign: 'left', textTransform: 'capitalize'
                  }}
                >
                  {viewAsRole === r ? '\u2713 ' : ''}{r}
                </button>
              ))}
              {viewAsRole && (
                <button onClick={() => setViewAsRole(null)}
                  style={{
                    display: 'block', width: '100%', padding: '6px 10px', marginTop: 8,
                    background: '#dc2626', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                    textAlign: 'center', fontWeight: 600
                  }}
                >
                  {'\u2190'} Back to Owner
                </button>
              )}
            </div>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-card" onClick={() => navigate("/profile")} style={{ cursor: "pointer" }}>
            <div className="user-avatar">{userInitials}</div>
            {sidebarOpen && (
              <div className="user-info">
                <span className="user-name">{user?.full_name || user?.email?.split("@")[0]}</span>
                <span className="user-role">{viewAsRole ? `Viewing as: ${viewAsRole}` : role}</span>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <>
              <LanguageSelector />
              <div style={{ height: 8 }} />
              <button className="sign-out-btn" onClick={handleSignOut}>Sign Out</button>
            </>
          )}
        </div>
      </aside>
      <main className={"main-content " + (sidebarOpen ? "" : "expanded")}>
        <header className="top-bar">
          <div className="top-bar-left">
            <h2 className="page-title">
              {filteredSections.flatMap(s => s.items).find(i => i.path === location.pathname)?.label || "Dashboard"}
            </h2>
          </div>
          <div className="top-bar-right">
            <span className="role-badge">{viewAsRole ? `VIEWING: ${viewAsRole.toUpperCase()}` : role.toUpperCase()}</span>
            <span className="user-email">{user?.email}</span>
          </div>
        </header>
        <div className="page-content">
          {children}
        </div>
      </main>
    </div>
  );
}